import { useEffect, useRef, useCallback } from 'react';

const useAutoScroll = () => {
  const animationFrameRef = useRef(null);
  const isDraggingRef = useRef(false);
  const lastClientYRef = useRef(0);
  const velocityRef = useRef(0);

  const startAutoScroll = useCallback((clientY) => {
    if (!isDraggingRef.current) {
      return;
    }

    lastClientYRef.current = clientY;

    // Cancel existing animation frame
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    const viewportHeight = window.innerHeight;
    const scrollThreshold = 150; // Reduced for smoother activation
    const maxScrollSpeed = 12; // Maximum pixels per frame
    const acceleration = 0.8; // How quickly speed ramps up

    const distanceFromTop = clientY;
    const distanceFromBottom = viewportHeight - clientY;

    let targetVelocity = 0;

    if (distanceFromTop < scrollThreshold) {
      // Scroll up - velocity based on distance from edge
      const intensity = (scrollThreshold - distanceFromTop) / scrollThreshold;
      targetVelocity = -maxScrollSpeed * Math.pow(intensity, 2);
    } else if (distanceFromBottom < scrollThreshold) {
      // Scroll down - velocity based on distance from edge
      const intensity = (scrollThreshold - distanceFromBottom) / scrollThreshold;
      targetVelocity = maxScrollSpeed * Math.pow(intensity, 2);
    }

    const animate = () => {
      if (!isDraggingRef.current) {
        velocityRef.current = 0;
        return;
      }

      // Smooth velocity transition
      velocityRef.current += (targetVelocity - velocityRef.current) * acceleration;

      // Only scroll if velocity is significant enough
      if (Math.abs(velocityRef.current) > 0.1) {
        window.scrollBy(0, velocityRef.current);
      }

      // Continue animation if we're still in scroll zone
      if (targetVelocity !== 0) {
        animationFrameRef.current = requestAnimationFrame(animate);
      } else {
        // Gradually slow down when leaving scroll zone
        if (Math.abs(velocityRef.current) > 0.1) {
          velocityRef.current *= 0.9; // Friction
          animationFrameRef.current = requestAnimationFrame(animate);
        } else {
          velocityRef.current = 0;
        }
      }
    };

    animationFrameRef.current = requestAnimationFrame(animate);
  }, []);

  const stopAutoScroll = useCallback(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    velocityRef.current = 0;
  }, []);

  const startDragging = useCallback(() => {
    isDraggingRef.current = true;
    velocityRef.current = 0;

    const handleMouseMove = (e) => {
      startAutoScroll(e.clientY);
    };

    const handleMouseUp = () => {
      stopDragging();
    };

    const handleDragOver = (e) => {
      e.preventDefault(); // Prevent default drag behavior
      startAutoScroll(e.clientY);
    };

    // Use passive listeners for better performance
    document.addEventListener('mousemove', handleMouseMove, { passive: true });
    document.addEventListener('mouseup', handleMouseUp, { passive: true });
    document.addEventListener('dragover', handleDragOver);

    // Store cleanup function
    isDraggingRef.cleanup = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('dragover', handleDragOver);
    };
  }, [startAutoScroll]);

  const stopDragging = useCallback(() => {
    isDraggingRef.current = false;
    stopAutoScroll();

    if (isDraggingRef.cleanup) {
      isDraggingRef.cleanup();
      isDraggingRef.cleanup = null;
    }
  }, [stopAutoScroll]);

  useEffect(() => {
    return () => {
      stopDragging();
      stopAutoScroll();
    };
  }, [stopDragging, stopAutoScroll]);

  return {
    startDragging,
    stopDragging
  };
};

export default useAutoScroll;
